import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Image as ImageIcon, Trash2, Plus, X, Save, Loader2, Type } from "lucide-react";

/**
 * Wraps any home item (category card, testimonial, etc.) with a small hover-visible
 * WordPress-style toolbar that lets admins edit or delete the content inline.
 *
 * Only renders extra chrome when:
 *   - user is admin
 *   - document.body has class "admin-edit-mode" (edit mode toggle)
 *
 * Props:
 *   children: the visual element
 *   onEditTitle: (newLabel) => void
 *   onEditImage: (newImgUrl) => void
 *   onDelete: () => void
 *   title, image: current values (for dialog defaults)
 */
export function InlineEditableItem({ children, onEditTitle, onEditImage, onDelete, title = "", image = "", label = "elemento" }) {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [dialog, setDialog] = useState(null); // "title" | "image" | null

  useEffect(() => {
    const check = () => setEditMode(document.body.classList.contains("admin-edit-mode"));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  const isActive = user?.is_admin && editMode;
  if (!isActive) return children;

  return (
    <div className="relative group/inline-edit outline outline-2 outline-dashed outline-[#22ff66]/50 outline-offset-[-6px]">
      {children}
      {/* Hover toolbar */}
      <div
        data-testid="inline-actions-toolbar"
        className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex gap-1 bg-black/85 backdrop-blur-md p-1 opacity-0 group-hover/inline-edit:opacity-100 transition"
        onClick={(e) => e.preventDefault()}
      >
        {onEditTitle && (
          <ToolBtn testid="inline-edit-title" icon={Type} label="Cambia titolo" onClick={(e) => { e.preventDefault(); setDialog("title"); }} />
        )}
        {onEditImage && (
          <ToolBtn testid="inline-edit-image" icon={ImageIcon} label="Cambia foto" onClick={(e) => { e.preventDefault(); setDialog("image"); }} />
        )}
        {onDelete && (
          <ToolBtn testid="inline-delete" icon={Trash2} label="Elimina" danger onClick={(e) => {
            e.preventDefault();
            if (window.confirm(`Eliminare "${title || label}"?`)) onDelete();
          }} />
        )}
      </div>

      {dialog === "title" && (
        <InlineTextDialog
          initial={title}
          onSave={(v) => { onEditTitle(v); setDialog(null); }}
          onClose={() => setDialog(null)}
          title="Cambia titolo"
          placeholder="Es. SUV"
        />
      )}
      {dialog === "image" && (
        <InlineImageDialog
          initial={image}
          onSave={(v) => { onEditImage(v); setDialog(null); }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function ToolBtn({ testid, icon: Icon, label, onClick, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      title={label}
      className={`w-8 h-8 flex items-center justify-center transition text-white ${danger ? "hover:bg-[#22ff66]" : "hover:bg-[#22ff66]/80"}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

/** Simple centered inline dialog for renaming a title. */
function InlineTextDialog({ initial, onSave, onClose, title = "Modifica testo", placeholder = "" }) {
  const [v, setV] = useState(initial || "");
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="inline-text-dialog"
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-black/10 shadow-2xl w-full max-w-md p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-uc text-lg">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 hover:bg-black/5 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          data-testid="inline-text-input"
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder={placeholder}
          className="w-full h-12 border border-black/15 px-3 text-sm uppercase tracking-widest text-black focus:border-[#22ff66] outline-none"
          onKeyDown={(e) => { if (e.key === "Enter") onSave(v); }}
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 h-11 border border-black/15 hover:bg-black/5 uppercase text-xs tracking-widest font-semibold">Annulla</button>
          <button
            data-testid="inline-text-save"
            onClick={() => onSave(v)}
            className="flex-1 h-11 bg-[#22ff66] hover:bg-[#4EFF87] text-black uppercase text-xs tracking-widest font-bold flex items-center justify-center gap-2"
          >
            <Save className="w-3.5 h-3.5" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}

/** Dialog to change image URL with upload option. */
function InlineImageDialog({ initial, onSave, onClose }) {
  const [url, setUrl] = useState(initial || "");
  const [uploading, setUploading] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !/\.(heic|heif|avif|svg)$/i.test(file.name || "")) {
      toast.error("Seleziona un'immagine (jpg, png, webp, heic, avif, svg)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // NOTE: DO NOT set Content-Type manually — axios will add the multipart boundary.
      const r = await api.post("/admin/upload/image", fd);
      setUrl(r.data.url);
      toast.success("Immagine caricata");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
      toast.error(`Upload fallito: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="inline-image-dialog"
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-black/10 shadow-2xl w-full max-w-lg p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-uc text-lg">Cambia foto</h3>
          <button onClick={onClose} className="w-8 h-8 hover:bg-black/5 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-3">
          <input
            data-testid="inline-image-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="URL immagine oppure carica sotto"
            className="w-full h-11 border border-black/15 px-3 text-sm text-black focus:border-[#22ff66] outline-none"
          />
          <label className="flex items-center justify-center gap-2 h-11 border border-black/15 hover:bg-black/5 cursor-pointer text-xs uppercase tracking-widest font-semibold">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            {uploading ? "Caricamento..." : "Carica da computer"}
            <input
              data-testid="inline-image-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => doUpload(e.target.files?.[0])}
            />
          </label>
          {url && (
            <img src={url} alt="anteprima" className="w-full aspect-video object-cover border border-black/10" />
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 h-11 border border-black/15 hover:bg-black/5 uppercase text-xs tracking-widest font-semibold">Annulla</button>
          <button
            data-testid="inline-image-save"
            onClick={() => onSave(url)}
            disabled={!url}
            className="flex-1 h-11 bg-[#22ff66] hover:bg-[#4EFF87] text-black uppercase text-xs tracking-widest font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}

/** Card that admins can click to add a new item (visible only in edit mode). */
export function AddInlineItem({ label = "Aggiungi contenuto", onClick, testid = "inline-add", aspect = "aspect-[3/4]" }) {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  useEffect(() => {
    const check = () => setEditMode(document.body.classList.contains("admin-edit-mode"));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);
  if (!user?.is_admin || !editMode) return null;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`${aspect} border-2 border-dashed border-[#22ff66] bg-[#22ff66]/5 hover:bg-[#22ff66]/15 flex flex-col items-center justify-center gap-2 transition text-[#22ff66] font-bold uppercase text-xs tracking-widest`}
    >
      <Plus className="w-8 h-8" />
      {label}
    </button>
  );
}
